<template>
  <model-form
    ref="modelForm"
    :model="tipPoolType"
    modal-type="modal"
  >
    <q-tabs
      v-model="tab"
      active-color="primary"
    >
      <q-tab
        name="details"
        label="Details"
      />
      <q-tab
        name="schedule"
        label="Schedule"
      />
      <q-tab
        name="strategy"
        label="Strategy"
      />
    </q-tabs>
    <div class="column">
      <div class="row q-col-gutter-sm items-center q-mt-sm">
        <model-form-input
          field="name"
          class="col-12"
          label="Name"
          autofocus
          :rules="[val => !!val || 'Field is required']"
        />
      </div>
      <q-tab-panels
        v-model="tab"
        animated
      >
        <q-tab-panel
          name="details"
          class="q-pa-none"
        >
          <div class="row q-col-gutter-sm items-center">
            <model-form-select
              field="distributionSchedule"
              class="col-6"
              label="Distribution Schedule"
              :options="$mh.asOptions(SCHEDULES.ALL)"
              map-options
              emit-value
              :rules="[val => !$_.isNil(val) || 'Field is required']"
            >
              <template #option="scope">
                <q-item
                  v-bind="scope.itemProps"
                  v-on="scope.itemEvents"
                >
                  <q-item-section>
                    <div class="column">
                      <span
                        class="text-body-em"
                        style="font-size: 0.9rem;"
                      >{{ scope.opt.label }}</span>
                      <span class="text-caption">{{ SCHEDULES_TOOLTIPS[scope.opt.value] }}</span>
                    </div>
                  </q-item-section>
                </q-item>
              </template>
              <q-tooltip
                anchor="top middle"
                self="bottom middle"
              >
                Tip Pools will be distributed according to the selected schedule. Determines when tips are distributed.
              </q-tooltip>
            </model-form-select>
            <model-form-select
              field="distributionStrategy"
              class="col-6"
              label="Distribution Strategy"
              :options="$mh.asOptions(STRATEGIES.ALL)"
              map-options
              emit-value
              :rules="[val => !$_.isNil(val) || 'Field is required']"
            >
              <template #option="scope">
                <q-item
                  v-bind="scope.itemProps"
                  v-on="scope.itemEvents"
                >
                  <q-item-section>
                    <div class="column">
                      <span
                        class="text-body-em"
                        style="font-size: 0.9rem;"
                      >{{ scope.opt.label }}</span>
                      <span class="text-caption">{{ STRATEGIES_TOOLTIPS[scope.opt.value] }}</span>
                    </div>
                  </q-item-section>
                </q-item>
              </template>
              <q-tooltip
                anchor="top middle"
                self="bottom middle"
              >
                Tip Pools will be distributed according to the selected strategy. Determines how tips are distributed.
              </q-tooltip>
            </model-form-select>
            <model-form-select
              field="employeeJobTypeIds"
              class="col-12"
              label="Job Types"
              :options="employeeJobTypes.data"
              option-label="name"
              option-value="id"
              map-options
              emit-value
              multiple
              use-chips
              clearable
              :rules="[
                val => !$_.isNil(val) || 'Field is required',
                val => !$_.isEmpty(val) || 'Must select one or more job types'
              ]"
            />
          </div>
        </q-tab-panel>
        <q-tab-panel
          name="schedule"
          class="q-pa-none"
        >
          <div class="row q-col-gutter-sm items-center">
            <model-form-select
              field="distributionSchedule"
              class="col-12"
              label="Distribution Schedule"
              :options="$mh.asOptions(SCHEDULES.ALL)"
              map-options
              emit-value
              :rules="[val => !$_.isNil(val) || 'Field is required']"
            >
              <template #option="scope">
                <q-item
                  v-bind="scope.itemProps"
                  v-on="scope.itemEvents"
                >
                  <q-item-section>
                    <div class="column">
                      <span
                        class="text-body-em"
                        style="font-size: 0.9rem;"
                      >{{ scope.opt.label }}</span>
                      <span class="text-caption">{{ SCHEDULES_TOOLTIPS[scope.opt.value] }}</span>
                    </div>
                  </q-item-section>
                </q-item>
              </template>
              <q-tooltip
                anchor="top middle"
                self="bottom middle"
              >
                Tip Pools will be distributed according to the selected schedule. Determines when tips are distributed.
              </q-tooltip>
            </model-form-select>
            <model-form-select
              v-if="!$_.isEmpty(SCHEDULE_CONDITIONS[tipPoolType.distributionSchedule])"
              field="distributionScheduleConditions"
              class="col-12"
              label="Schedule Conditions"
              :options="$mh.asOptions(SCHEDULE_CONDITIONS[tipPoolType.distributionSchedule])"
              map-options
              emit-value
              multiple
              use-chips
              clearable
              :decode="conditions => $mh.objectToArray(conditions)"
              :encode="enabledConditions => $mh.arrayToObject(enabledConditions || [], { allKeys: SCHEDULE_CONDITIONS[tipPoolType.distributionSchedule] })"
            >
              <template #option="scope">
                <q-item
                  v-bind="scope.itemProps"
                  v-on="scope.itemEvents"
                >
                  <q-item-section>
                    <div class="column">
                      <span
                        class="text-body-em"
                        style="font-size: 0.9rem;"
                      >{{ scope.opt.label }}</span>
                      <span class="text-caption">{{ SCHEDULES_CONDITIONS_TOOLTIPS[scope.opt.value] }}</span>
                    </div>
                  </q-item-section>
                </q-item>
              </template>
              <q-tooltip
                anchor="top middle"
                self="bottom middle"
              >
                Tip Pools will be distributed according to the selected schedule conditions.
              </q-tooltip>
            </model-form-select>
            <model-form-select
              v-if="tipPoolType.distributionScheduleConditions[SCHEDULE_CONDITIONS.DAY_OF_WEEK]"
              :field="`distributionScheduleConditionsRequirements.${SCHEDULE_CONDITIONS_REQUIREMENTS.DAY_OF_WEEK}`"
              class="col-6"
              label="Distribution Day"
              value-type="Number"
              :options="[
                { label: 'Monday', value: 1 },
                { label: 'Tuesday', value: 2 },
                { label: 'Wednesday', value: 3 },
                { label: 'Thursday', value: 4 },
                { label: 'Friday', value: 5 },
                { label: 'Saturday', value: 6 },
                { label: 'Sunday', value: 0 }
              ]"
              map-options
              emit-value
              :rules="[val => !$_.isNil(val) || 'Field is required']"
            />
            <model-form-input
              v-if="tipPoolType.distributionScheduleConditions[SCHEDULE_CONDITIONS.TIME_OF_DAY]"
              :field="`distributionScheduleConditionsRequirements.${SCHEDULE_CONDITIONS_REQUIREMENTS.TIME_OF_DAY}`"
              class="col-6"
              label="Distribution Time"
              value-type="String"
              :rules="[
                val => !$_.isNil(val) || 'Field is required'
              ]"
              @click="showDistributionClock = true"
            >
              <template #append>
                <q-icon
                  name="access_time"
                  class="cursor-pointer"
                >
                  <q-popup-proxy
                    v-model="showDistributionClock"
                    transition-show="scale"
                    transition-hide="scale"
                  >
                    <q-time
                      v-model="tipPoolType.distributionScheduleConditionsRequirements[SCHEDULE_CONDITIONS_REQUIREMENTS.TIME_OF_DAY]"
                      :minute-options="[0, 15, 30, 45]"
                    >
                      <div class="row items-center justify-end">
                        <q-btn
                          v-close-popup
                          label="Close"
                          color="primary"
                          flat
                        />
                      </div>
                    </q-time>
                  </q-popup-proxy>
                </q-icon>
              </template>
            </model-form-input>
            <model-form-input
              v-if="tipPoolType.distributionScheduleConditions[SCHEDULE_CONDITIONS.DELAY_DISTRIBUTION]"
              :field="`distributionScheduleConditionsRequirements.${SCHEDULE_CONDITIONS_REQUIREMENTS.DELAY_DISTRIBUTION_HOURS}`"
              class="col-6"
              label="Delay Distribution Hours"
              value-type="Number"
              min="1"
              max="48"
              step="1"
              :rules="[
                val => !$_.isNil(val) || 'Field is required',
                val => val >= 1 && val <= 48 || 'Must be between 1 and 48'
              ]"
            />
          </div>
        </q-tab-panel>
        <q-tab-panel
          name="strategy"
          class="q-pa-none"
        >
          <div class="row q-col-gutter-sm items-center">
            <model-form-select
              field="distributionStrategy"
              class="col-12"
              label="Distribution Strategy"
              :options="$mh.asOptions(STRATEGIES.ALL)"
              map-options
              emit-value
              :rules="[val => !$_.isNil(val) || 'Field is required']"
            >
              <template #option="scope">
                <q-item
                  v-bind="scope.itemProps"
                  v-on="scope.itemEvents"
                >
                  <q-item-section>
                    <div class="column">
                      <span
                        class="text-body-em"
                        style="font-size: 0.9rem;"
                      >{{ scope.opt.label }}</span>
                      <span class="text-caption">{{ STRATEGIES_TOOLTIPS[scope.opt.value] }}</span>
                    </div>
                  </q-item-section>
                </q-item>
              </template>
              <q-tooltip
                anchor="top middle"
                self="bottom middle"
              >
                Tip Pools will be distributed according to the selected strategy. Determines how tips are distributed.
              </q-tooltip>
            </model-form-select>
            <model-form-select
              v-if="!$_.isEmpty(STRATEGY_CONDITIONS[tipPoolType.distributionStrategy])"
              field="distributionStrategyConditions"
              class="col-12"
              label="Strategy Conditions"
              :options="$mh.asOptions(STRATEGY_CONDITIONS[tipPoolType.distributionStrategy])"
              map-options
              emit-value
              multiple
              use-chips
              clearable
              :decode="conditions => $mh.objectToArray(conditions)"
              :encode="enabledConditions => $mh.arrayToObject(enabledConditions || [], { allKeys: STRATEGY_CONDITIONS[tipPoolType.distributionStrategy] })"
            >
              <template #option="scope">
                <q-item
                  v-bind="scope.itemProps"
                  v-on="scope.itemEvents"
                >
                  <q-item-section>
                    <div class="column">
                      <span
                        class="text-body-em"
                        style="font-size: 0.9rem;"
                      >{{ scope.opt.label }}</span>
                      <span class="text-caption">{{ STRATEGIES_CONDITIONS_TOOLTIPS[scope.opt.value] }}</span>
                    </div>
                  </q-item-section>
                </q-item>
              </template>
              <q-tooltip
                anchor="top middle"
                self="bottom middle"
              >
                Tip Pools will be distributed according to the selected strategy conditions.
              </q-tooltip>
            </model-form-select>
            <model-form-input
              v-if="tipPoolType.distributionStrategyConditions[STRATEGY_CONDITIONS.REQUIRED_HOURS]"
              :field="`distributionStrategyConditionsRequirements.${STRATEGY_CONDITIONS_REQUIREMENTS.REQUIRED_HOURS_MINIMUM}`"
              class="col-6"
              label="Required Hours Minimum"
              value-type="Number"
              min="1"
              step="1"
              :rules="[
                val => !$_.isNil(val) || 'Field is required',
                val => val >= 1 && val <= 48 || 'Must be at least 1'
              ]"
            />
          </div>
        </q-tab-panel>
      </q-tab-panels>
    </div>
  </model-form>
</template>

<script>
import { SCHEDULE_CONDITIONS, SCHEDULE_CONDITIONS_REQUIREMENTS, SCHEDULES, STRATEGIES, STRATEGY_CONDITIONS, STRATEGY_CONDITIONS_REQUIREMENTS } from 'src/models/tip/TipPoolType'
import ModelForm from 'src/modules/admin/components/ModelForm/ModelForm'

const SCHEDULES_TOOLTIPS = {
  [SCHEDULES.IMMEDIATE]: 'The tip pool will be distributed shortly  after the tip is shared.',
  [SCHEDULES.DAILY]: 'The tip pool will be distributed daily at the end of the day.',
  [SCHEDULES.WEEKLY]: 'The tip pool will be distributed weekly at the end of the week.'
}

const SCHEDULES_CONDITIONS_TOOLTIPS = {
  [SCHEDULE_CONDITIONS.DELAY_DISTRIBUTION]: 'Tip pools will be wait to be distributed for a given number of hours after the pool is closed.',
  [SCHEDULE_CONDITIONS.DAY_OF_WEEK]: 'Weekly tip pools will open and close on the given day of the week.',
  [SCHEDULE_CONDITIONS.TIME_OF_DAY]: 'Daily and Weekly tip pools will open and close at a given time of the day.'
}

const STRATEGIES_TOOLTIPS = {
  [STRATEGIES.CLOCKED_IN]: 'The tip pool will be distributed to employees who were clocked in when the tip was paid.',
  [STRATEGIES.PROPORTIONAL]: 'The tip pool will be distributed proportionally based on hours worked during the tip pool.'
}

const STRATEGIES_CONDITIONS_TOOLTIPS = {
  [STRATEGY_CONDITIONS.REQUIRED_HOURS]: 'Only distribute to employees who worked sufficient hours within the duration of the tip pool.'
}

export default {
  name: 'TipPoolTypeForm',
  components: {
    ModelForm
  },
  configuration: ['employeeJobTypes'],
  models: {
    tipPoolType: {
      employeeJobTypes: {
        fields: ['id', 'name']
      }
    }
  },
  data () {
    return {
      tab: 'details',
      showDistributionClock: false
    }
  },
  watch: {
    'tipPoolType.distributionSchedule' () {
      const excludedScheduleConditions = _.without(SCHEDULE_CONDITIONS.ALL, ...SCHEDULE_CONDITIONS[this.tipPoolType.distributionSchedule])
      _.each(excludedScheduleConditions, scheduleCondition => { this.tipPoolType.distributionScheduleConditions[scheduleCondition] = false })
    },
    'tipPoolType.distributionStrategy' () {
      const excludedStrategyConditions = _.without(STRATEGY_CONDITIONS.ALL, ...STRATEGY_CONDITIONS[this.tipPoolType.distributionStrategy])
      _.each(excludedStrategyConditions, strategyCondition => { this.tipPoolType.distributionStrategyConditions[strategyCondition] = false })
    }
  },
  created () {
    _.extend(this, {
      SCHEDULES,
      SCHEDULES_TOOLTIPS,
      SCHEDULE_CONDITIONS,
      SCHEDULE_CONDITIONS_REQUIREMENTS,
      SCHEDULES_CONDITIONS_TOOLTIPS,
      STRATEGIES,
      STRATEGIES_TOOLTIPS,
      STRATEGY_CONDITIONS,
      STRATEGY_CONDITIONS_REQUIREMENTS,
      STRATEGIES_CONDITIONS_TOOLTIPS
    })
  }
}
</script>
